import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ReferenceCategory,
  ReferenceLinkTargetType,
  ReferenceSort,
  type ReferenceCategory as Category,
  type ReferenceLinkTargetType as TargetType,
  type ReferenceSort as Sort,
} from '@scoutaround/script-breakdown-shared';

import type { ReferenceLibraryQuery } from '../../lib/api.js';

/**
 * THE LIBRARY'S STATE IS ITS ADDRESS:
 * `?ref=&group=&q=&cat=&linked=&linkedId=&discussed=1&sort=&view=grid|list`.
 * A reload, a shared link and Back all land on the same view (AC13). Values are
 * written lower case for a readable address and read case-insensitively; a value
 * that is not in the vocabulary reads as absent rather than breaking the page.
 * Other parameters on the page — the conversation drawer's `discuss` and
 * `thread` — are kept.
 */

export type ReferenceView = 'grid' | 'list';
export type LinkedFilter = TargetType | 'ANY';

export interface ReferenceAddress {
  readonly ref: string | null;
  readonly group: string | null;
  readonly q: string;
  readonly cat: Category | null;
  readonly linked: LinkedFilter | null;
  readonly linkedId: string | null;
  readonly discussed: boolean;
  readonly sort: Sort;
  readonly view: ReferenceView;
}

export type ReferenceAddressChange = {
  -readonly [K in keyof ReferenceAddress]?: ReferenceAddress[K] | null;
};

function parseEnum<T extends string>(raw: string | null, parse: (value: string) => T | null): T | null {
  if (raw === null || raw === '') return null;
  return parse(raw.toUpperCase());
}

export function parseReferenceAddress(params: URLSearchParams): ReferenceAddress {
  const linked = parseEnum<LinkedFilter>(params.get('linked'), (v) => {
    if (v === 'ANY') return 'ANY';
    const parsed = ReferenceLinkTargetType.safeParse(v);
    return parsed.success ? parsed.data : null;
  });
  const linkedId = params.get('linkedId');

  return {
    ref: params.get('ref') || null,
    group: params.get('group') || null,
    q: params.get('q') ?? '',
    cat: parseEnum(params.get('cat'), (v) => {
      const parsed = ReferenceCategory.safeParse(v);
      return parsed.success ? parsed.data : null;
    }),
    linked,
    // An item id means something only beside its type.
    linkedId: linked !== null && linked !== 'ANY' && linkedId ? linkedId : null,
    discussed: params.get('discussed') === '1',
    sort:
      parseEnum(params.get('sort'), (v) => {
        const parsed = ReferenceSort.safeParse(v);
        return parsed.success ? parsed.data : null;
      }) ?? 'RECENT',
    view: params.get('view') === 'list' ? 'list' : 'grid',
  };
}

/** Write one change into a copy of the parameters, dropping defaults so the address stays short. */
export function applyReferenceChange(current: URLSearchParams, change: ReferenceAddressChange): URLSearchParams {
  const next = new URLSearchParams(current);
  const set = (key: string, value: string | null) => {
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  };

  if ('ref' in change) set('ref', change.ref ?? null);
  if ('group' in change) set('group', change.group ?? null);
  if ('q' in change) set('q', change.q?.trim() ? change.q : null);
  if ('cat' in change) set('cat', change.cat ? change.cat.toLowerCase() : null);
  if ('linked' in change) {
    set('linked', change.linked ? change.linked.toLowerCase() : null);
    if (!change.linked || change.linked === 'ANY') next.delete('linkedId');
  }
  if ('linkedId' in change) set('linkedId', change.linkedId ?? null);
  if ('discussed' in change) set('discussed', change.discussed === true ? '1' : null);
  if ('sort' in change) set('sort', change.sort && change.sort !== 'RECENT' ? change.sort.toLowerCase() : null);
  if ('view' in change) set('view', change.view === 'list' ? 'list' : null);
  return next;
}

export function useReferenceAddress() {
  const [params, setParams] = useSearchParams();
  const address = useMemo(() => parseReferenceAddress(params), [params]);

  /**
   * `push` for a step somebody would walk back with Back (opening a reference);
   * `replace` for refining what the list shows, so typing a search is not twenty
   * history entries.
   */
  const update = useCallback(
    (change: ReferenceAddressChange, options: { push?: boolean } = {}) => {
      setParams((current) => applyReferenceChange(current, change), { replace: options.push !== true });
    },
    [setParams],
  );

  return { address, update };
}

/** What the list call is asked for, from the address. */
export function libraryQueryFrom(address: ReferenceAddress): ReferenceLibraryQuery {
  return {
    ...(address.q.trim() !== '' ? { q: address.q.trim() } : {}),
    ...(address.group !== null ? { group: address.group } : {}),
    ...(address.cat !== null ? { category: address.cat } : {}),
    ...(address.linked !== null ? { linked: address.linked } : {}),
    ...(address.linkedId !== null ? { linkedId: address.linkedId } : {}),
    ...(address.discussed ? { discussed: '1' as const } : {}),
    sort: address.sort,
  };
}

/** Anything narrowing the list besides the group. */
export function hasNarrowing(address: ReferenceAddress): boolean {
  return address.q.trim() !== '' || address.cat !== null || address.linked !== null || address.discussed;
}
